import {
  Dropbox,
  dropbox_os,
  DropboxDownloadResult,
  DropboxRequestOptions,
  DropboxResponse,
} from '@dropbox/api-v2-client';
import {ReplitCatalogPage, ReplitCatalogService} from 'client';

import {getAppPlatformEnvironment} from './appPlatformEnvironment';

export const APPS_PAGE_SIZE = 50;
export const appsQueryKey = ['apps'] as const;
export const allAppsQueryKey = [...appsQueryKey, 'all'] as const;
export const myAppsQueryKey = [...appsQueryKey, 'mine'] as const;
export const allReplitAppsQueryKey = [...appsQueryKey, 'replit', 'all'] as const;
export const myReplitAppsQueryKey = [...appsQueryKey, 'replit', 'mine'] as const;

type EnvironmentRoutedArg<T extends {environment?: dropbox_os.AppPlatformEnvironment}> = Omit<
  T,
  'environment'
>;

export async function createSite(
  client: Dropbox,
  arg: EnvironmentRoutedArg<dropbox_os.CreateSiteArg>
): Promise<dropbox_os.CreateSiteResponse> {
  const response: DropboxResponse<dropbox_os.CreateSiteResponse> = await client.dropboxOsCreateSite(
    {...arg, environment: getAppPlatformEnvironment()}
  );

  return response.result;
}

export async function beginAssetUpload(
  client: Dropbox,
  arg: EnvironmentRoutedArg<dropbox_os.BeginAssetUploadArg>
): Promise<dropbox_os.BeginAssetUploadResponse> {
  const response: DropboxResponse<dropbox_os.BeginAssetUploadResponse> =
    await client.dropboxOsBeginAssetUpload({...arg, environment: getAppPlatformEnvironment()});

  return response.result;
}

export async function completeAssetUpload(
  client: Dropbox,
  arg: EnvironmentRoutedArg<dropbox_os.CompleteAssetUploadArg>
): Promise<dropbox_os.CompleteAssetUploadResponse> {
  const response: DropboxResponse<dropbox_os.CompleteAssetUploadResponse> =
    await client.dropboxOsCompleteAssetUpload({...arg, environment: getAppPlatformEnvironment()});

  return response.result;
}

export async function downloadAppScreenshot(
  client: Dropbox,
  arg: EnvironmentRoutedArg<dropbox_os.DownloadAppScreenshotArg>,
  options?: DropboxRequestOptions
): Promise<Blob> {
  const response: DropboxResponse<DropboxDownloadResult<dropbox_os.DownloadAppScreenshotResult>> =
    await client.dropboxOsDownloadAppScreenshot(
      {...arg, environment: getAppPlatformEnvironment()},
      options
    );
  const {fileBlob} = response.result;

  if (!fileBlob) {
    throw new Error('Screenshot download did not return a browser Blob');
  }

  if (fileBlob.type === 'image/png') {
    return fileBlob as Blob;
  }

  return fileBlob.slice(0, fileBlob.size, 'image/png') as Blob;
}

export async function getSitePublicationState(
  client: Dropbox,
  arg: EnvironmentRoutedArg<dropbox_os.GetSitePublicationStateArg>
): Promise<dropbox_os.GetSitePublicationStateResponse> {
  const response: DropboxResponse<dropbox_os.GetSitePublicationStateResponse> =
    await client.dropboxOsGetSitePublicationState({
      ...arg,
      environment: getAppPlatformEnvironment(),
    });

  return response.result;
}

export async function publishDeployment(
  client: Dropbox,
  arg: EnvironmentRoutedArg<dropbox_os.PublishDeploymentArg>
): Promise<dropbox_os.DeploymentPublicationResponse> {
  const response: DropboxResponse<dropbox_os.DeploymentPublicationResponse> =
    await client.dropboxOsPublishDeployment({...arg, environment: getAppPlatformEnvironment()});

  return response.result;
}

export async function setPublishVisibility(
  client: Dropbox,
  arg: EnvironmentRoutedArg<dropbox_os.SetPublishVisibilityArg>
): Promise<dropbox_os.SetPublishVisibilityResponse> {
  const response: DropboxResponse<dropbox_os.SetPublishVisibilityResponse> =
    await client.dropboxOsSetPublishVisibility({
      ...arg,
      environment: getAppPlatformEnvironment(),
    });

  return response.result;
}

export async function retireSite(
  client: Dropbox,
  arg: EnvironmentRoutedArg<dropbox_os.RetireSiteArg>
): Promise<dropbox_os.RetireSiteResponse> {
  const response: DropboxResponse<dropbox_os.RetireSiteResponse> = await client.dropboxOsRetireSite(
    {...arg, environment: getAppPlatformEnvironment()}
  );

  return response.result;
}

const listAppsArg = (cursor?: string): dropbox_os.ListAppsArg => ({
  page_size: APPS_PAGE_SIZE,
  environment: getAppPlatformEnvironment(),
  ...(cursor ? {page_token: cursor} : {}),
});

export async function listApps(
  client: Dropbox,
  cursor?: string
): Promise<dropbox_os.ListAppsResponse> {
  const response: DropboxResponse<dropbox_os.ListAppsResponse> = await client.dropboxOsListApps(
    listAppsArg(cursor)
  );

  return response.result;
}

export async function listMyApps(
  client: Dropbox,
  cursor?: string
): Promise<dropbox_os.ListAppsResponse> {
  const response: DropboxResponse<dropbox_os.ListAppsResponse> = await client.dropboxOsListMyApps(
    listAppsArg(cursor)
  );

  return response.result;
}

export const listReplitApps = (cursor?: string): Promise<ReplitCatalogPage> =>
  ReplitCatalogService.listReplitCatalogAppsApiV1ReplitCatalogAppsGet(APPS_PAGE_SIZE, cursor);

export const listMyReplitApps = (cursor?: string): Promise<ReplitCatalogPage> =>
  ReplitCatalogService.listMyReplitCatalogAppsApiV1ReplitCatalogMyAppsGet(APPS_PAGE_SIZE, cursor);
